import { z } from 'zod';

/**
 * Phone / WhatsApp, stored as digits with the country code (E.164 without "+"), e.g. 5511998887766.
 * Brazilian numbers can be typed the usual way — "(11) 99888-7766" — and get 55 in front;
 * anything else needs its country code ("+351 912 345 678").
 */
export function normalizePhone(input: string): string | null {
  const raw = input.trim();
  let digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  if (!raw.startsWith('+')) {
    digits = digits.replace(/^0+/, '');
    // DDD + number (10 or 11 digits): Brazil.
    if (digits.length === 10 || digits.length === 11) digits = `55${digits}`;
  }
  if (digits.startsWith('55')) {
    const local = digits.slice(2);
    // DDD 11–99 + landline (8 digits) or mobile (9 digits starting with 9).
    if (!/^[1-9][1-9](\d{8}|9\d{8})$/.test(local)) return null;
    return digits;
  }
  return digits.length >= 8 && digits.length <= 15 ? digits : null;
}

/** "(11) 99888-7766" for Brazil, "+351 912345678" otherwise. */
export function formatPhone(stored: string | null | undefined): string {
  if (!stored) return '—';
  if (stored.startsWith('55') && (stored.length === 12 || stored.length === 13)) {
    const ddd = stored.slice(2, 4);
    const n = stored.slice(4);
    return `(${ddd}) ${n.slice(0, n.length - 4)}-${n.slice(-4)}`;
  }
  return `+${stored}`;
}

/** Live mask while typing a Brazilian number; leaves international ("+…") input alone. */
export function maskPhoneInput(value: string): string {
  if (value.trim().startsWith('+')) return value.replace(/[^\d+\s-]/g, '').slice(0, 20);
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

export const whatsappLink = (stored: string) => `https://wa.me/${stored}`;

export const phoneSchema = z
  .string()
  .trim()
  .max(30)
  .transform((v, ctx) => {
    const p = normalizePhone(v);
    if (!p) {
      ctx.addIssue({ code: 'custom', message: 'Telefone inválido. Use DDD + número, ex.: (11) 99999-9999' });
      return z.NEVER;
    }
    return p;
  });
